/**
 * Lightweight toast/notification queue.
 *
 * There was no global feedback surface before live trading; order
 * fills / rejections / risk-blocks need one ("Buy 0.10 XAUUSD @ 4585.30
 * filled" / "Rejected: not enough money"). Any component can
 * `dispatch(pushToast(...))`; the <Toaster> at the app root renders the
 * stack and auto-dismisses each after its ttl.
 *
 * Ids come from a monotonic counter in state (not Date.now/random) so
 * reducers stay pure and deterministic.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type ToastKind = "success" | "error" | "warning" | "info";

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  /** Optional second line (e.g. the broker's human-readable reason). */
  message?: string;
  /** Auto-dismiss after this many ms. 0 = sticky (manual close only). */
  ttlMs: number;
}

interface NotificationsState {
  items: Toast[];
  nextId: number;
}

const initialState: NotificationsState = {
  items: [],
  nextId: 1,
};

const DEFAULT_TTL = 5000;

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    pushToast: (
      state,
      action: PayloadAction<{
        kind: ToastKind;
        title: string;
        message?: string;
        ttlMs?: number;
      }>,
    ) => {
      const { kind, title, message, ttlMs } = action.payload;
      state.items.push({
        id: state.nextId,
        kind,
        title,
        message,
        // Errors linger a touch longer; explicit ttl wins.
        ttlMs: ttlMs ?? (kind === "error" ? 8000 : DEFAULT_TTL),
      });
      state.nextId += 1;
      // Cap the stack so a runaway loop can't grow it without bound.
      if (state.items.length > 6) state.items.shift();
    },
    dismissToast: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((t) => t.id !== action.payload);
    },
  },
});

export const { pushToast, dismissToast } = notificationsSlice.actions;
export default notificationsSlice.reducer;
